import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  PersistedVideoCard,
  PhotoCategoryDraft,
  PhotoItem,
  PropertyStatus,
  StaffRoleOption,
  WizardStepId,
  WizardVersionPayload,
} from "@/types";
import { DEFAULT_PROPERTY_PHOTO_CATEGORIES, PHOTOS_MAX_HERO } from "@/lib/constants";
import { WIZARD_STEPS } from "@/lib/wizard-steps";
import { extractFieldVariants, extractPlainFieldMap } from "@/lib/utils";

// Optional steps with no required fields are always "complete" per the API,
// even empty. Only checkmark them if there's real content.
const OPTIONAL_STEP_HAS_DATA: Partial<Record<WizardStepId, (data: WizardVersionPayload) => boolean>> = {
  estate: (data) => Boolean(
    data.estate?.interiorRooms || data.estate?.exteriorFeatures || (data.estate?.customFields?.length ?? 0) > 0,
  ),
  host: (data) => Boolean(data.host?.name),
  supplier: (data) => Boolean(data.supplier?.name || data.supplier?.contactEmail || data.supplier?.contactPhone),
  rating: (data) => data.rating?.westayRating != null,
  importantInformation: (data) => (data.importantInformation?.importantInformation ?? []).some((term) => !term.curated),
  safety: (data) => (data.safety?.safetyDevices ?? []).some((term) => term.value) || (data.safety?.safetyConsiderations ?? []).some((term) => term.value),
  accessibility: (data) => (data.accessibility?.accessibilityCommon ?? []).some((term) => term.value) || (data.accessibility?.accessibilityRooms ?? []).some((room) => room.items.some((term) => term.value)),
};

/**
 * Derives the step rail's checkmarks from a full wizard payload's steps[].
 * The old backend had one combined `cancellation` step and one combined
 * `houseRules` step covering what are now four separate frontend steps
 * (Policies, Important Information, Safety, Accessibility) — that required
 * synthesizing completion for three of them straight from the payload
 * instead of a real step key. The API now reports each of the four
 * separately (steps[].key: "policies"/"safety"/"accessibility"/
 * "importantInformation", matching the section names on GET
 * /property-versions/{vid}?section=...), so they're just ordinary entries in
 * the loop below now, gated by OPTIONAL_STEP_HAS_DATA like any other
 * optional step.
 */
function computeCompletedStepIds(apiData: WizardVersionPayload): WizardStepId[] {
  const ids: WizardStepId[] = [];
  (apiData.steps ?? []).forEach((s) => {
    if (!s.complete) return;
    const id = s.key as WizardStepId;
    const isOptional = WIZARD_STEPS.find((step) => step.id === id)?.optional;
    if (isOptional && !OPTIONAL_STEP_HAS_DATA[id]?.(apiData)) return;
    ids.push(id);
  });
  return ids;
}

export interface LanguageVariant {
  lang: { code: string; name: string; flag: string };
  value: string;
}

/**
 * One language's translation of a taxonomy term — a term has both a label and
 * a description, so (unlike the single-value `LanguageVariant` used for scalar
 * fields like check-in time) this carries both per language.
 */
export interface TermLanguageVariant {
  lang: { code: string; name: string; flag: string };
  label: string;
  description: string;
}

interface WizardState {
  propertyId: string | null;
  versionId: string | null;
  /** False once a version is no longer writable (e.g. approved/published). */
  editable: boolean;
  status: PropertyStatus | null;
  /** Step-rail checkmarks — only ever populated from a full (no `section`) hydrate. */
  completedStepIds: WizardStepId[];
  /**
   * Which version StepDataFetcher/WizardHydrator last fetched the full
   * payload for — they only fetch once per version, not once per step. A
   * plain module-level flag doesn't work here: nothing re-runs their effect
   * to notice it changed unless the component actually remounts, which
   * doesn't happen just from navigating between steps of the same version.
   * Keeping it in the store makes invalidating it reactive — any successful
   * step save calls `invalidateWizardHydration`, which clears this, and
   * because these components subscribe to it, their effect re-fires and
   * refetches immediately, no navigation required. Excluded from persistence
   * (see `partialize`) — a value from localStorage would wrongly skip the
   * fetch on a genuine fresh page load.
   */
  hydratedVersionId: string | null;
  markVersionHydrated: (vid: string) => void;
  invalidateWizardHydration: () => void;
  /**
   * In-session cache for step hooks (Amenities, Pricing, Cancellation, Rules,
   * Photos) that fetch fresh into local state on every mount instead of using
   * the persisted `draftProperty` — without this, an edit not yet saved to
   * the server (these all save once on Next) vanished the moment the user
   * navigated to another step and back. Keyed by an opaque string each hook
   * picks via `useStepSessionCache`. Not persisted (see `partialize`) — this
   * is same-session bookkeeping, not draft data.
   */
  stepSessionCache: Partial<Record<string, unknown>>;
  setStepSessionCache: (key: string, value: unknown) => void;
  setDraftIds: (propertyId: string, versionId: string) => void;
  /**
   * Replaces draftProperty's server-backed sections (about, location, basics,
   * estate, staff selection) with real data for this version. Called whenever
   * the active version changes — draftProperty is a single persisted blob, so
   * without this, switching properties in the same browser session leaks the
   * previous property's rooms/selections into the new one.
   */
  hydrateWizard: (apiData: WizardVersionPayload) => void;
  draftProperty: {
    name: string;
    code: string;
    description: string;
    subDescription: string;
    tagline: string;
    rating: number;
    /** `id` is the fixed local UI slot (1|2|3); `serverId` is the real row id, sent back to keep the row (and its translations) alive across saves. */
    features: { id: number; serverId?: string; icon: string; title: string; desc: string }[];
    location: {
      addressLine: string;
      city: string;
      country: string;
      region: string;
      postalCode: string;
      lat: string;
      lng: string;
      countryCode: string;
      placeId: string | Record<string, any> | null;
      formattedAddress: string;
      floor: string;
      unit: string;
      building: string;
      scenicViews: string[];
      /** Ids the server told us were `curated: false` (agent-typed) as of the last hydrate. Needed because a saved custom scenic view becomes a real, permanent taxonomy term — after that, it's indistinguishable from a seeded one by id alone, so "not in the fetched taxonomy group" stops being a reliable custom-detection heuristic. */
      customScenicViewIds: string[];
      /** Custom scenic views this property has deleted — hides them from the picker. Client-only: a saved custom view is a real, shared taxonomy term, so "removing" it can only ever mean "stop offering it on this property," never deleting the shared term itself. */
      removedScenicViewIds: string[];
      nearbyPlaces: {
        id: string;
        placeType: string;
        distance: string;
        details: string;
        icon: string;
        lat: string;
        lng: string;
      }[];
      locationFeatures: {
        id: string;
        name: string;
        description: string;
        isCustom: boolean;
        isSelected?: boolean;
      }[];
    };
    basics: {
      bedrooms: number;
      bathrooms: number;
      maxGuests: number;
      livingArea: string;
      landSize: string;
      floors: { id: string; name: string }[];
      /** `size` (e.g. "20 m² / 450 sq ft") is client-only — no field for it on CreateRoomDto/UpdateRoomDto/RoomResponse yet. */
      rooms: { id: string; name: string; floorId?: string; beds: Record<string, number>; size?: string }[];
    };
    estate?: {
      interiorRooms: string;
      exteriorFeatures: string;
      customFields: { id: string; heading: string; description: string }[];
      /**
       * Per-floor highlights, keyed by the floor's id from basics.floors — BE
       * has no field for this yet (confirmed against the live API), so this
       * never round-trips through hydrateWizard/patchPropertyEstate; it's
       * carried locally (still surviving refresh via this store's own
       * persistence) until BE ships real support.
       */
      floorFeatures: Record<string, { id: string; text: string; icon?: string }[]>;
    };
    supplier?: {
      name: string;
      contactEmail: string;
      contactPhone: string;
      rating: number;
      notes: string;
    };
    translations: {
      name: LanguageVariant[];
      description: LanguageVariant[];
      subDescription: LanguageVariant[];
      tagline: LanguageVariant[];
      /** Keyed by feature slot id (`String(feature.id)`, e.g. "1"/"2"/"3") — mirrors `customFields` below, one entry per feature description. */
      /** Keyed by feature slot id — each feature translates both its title and its subtext, shown together in one combined modal (FeatureTranslationsModal). */
      features: Record<string, { title: LanguageVariant[]; subtext: LanguageVariant[] }>;
      addressLine: LanguageVariant[];
      city: LanguageVariant[];
      country: LanguageVariant[];
      region: LanguageVariant[];
      postalCode: LanguageVariant[];
      formattedAddress: LanguageVariant[];
      floor: LanguageVariant[];
      unit: LanguageVariant[];
      building: LanguageVariant[];
      scenicViews: Record<string, LanguageVariant[]>;
      /** Keyed by nearby-place id — `placeType`/`distance`/`details` all translate with the place. */
      nearbyPlaces: Record<string, { placeType: LanguageVariant[]; distance: LanguageVariant[]; details: LanguageVariant[] }>;
      locationFeatures: Record<string, LanguageVariant[]>;
      floors: Record<string, LanguageVariant[]>;
      rooms: Record<string, LanguageVariant[]>;
      /** Keyed by floor-detail row id (not floor id) — a detail's id is already globally unique across floors. */
      floorFeatures: Record<string, LanguageVariant[]>;
      bedNames: Record<string, LanguageVariant[]>;
      interiorRooms: LanguageVariant[];
      exteriorFeatures: LanguageVariant[];
      customFields: Record<string, LanguageVariant[]>;
      /** Keyed by custom field id — the heading, distinct from customFields above (the description). Unlike that one, this reaches the API: CustomFieldDto.translations is generic and `label` is a real field on the row. */
      customFieldLabels: Record<string, LanguageVariant[]>;
      supplierName: LanguageVariant[];
      supplierNotes: LanguageVariant[];
      checkInTime: LanguageVariant[];
      checkOutTime: LanguageVariant[];
      quietHoursStart: LanguageVariant[];
      quietHoursEnd: LanguageVariant[];
      numberOfGuests: LanguageVariant[];
      maxPets: LanguageVariant[];
      additionalRules: LanguageVariant[];
      hostName: LanguageVariant[];
      hostLocation: LanguageVariant[];
      hostBio: LanguageVariant[];
      houseRules: Record<string, TermLanguageVariant[]>;
      safetyDevices: Record<string, TermLanguageVariant[]>;
      safetyConsiderations: Record<string, TermLanguageVariant[]>;
      accessibilityCommon: Record<string, TermLanguageVariant[]>;
      accessibilityRooms: Record<string, TermLanguageVariant[]>;
      paymentMethods: Record<string, TermLanguageVariant[]>;
      importantInformation: Record<string, TermLanguageVariant[]>;
    };
    photos: {
      propertyCategories: PhotoCategoryDraft[];
    };
    /** Photos step — serializable video card state so the success UI survives page refresh */
    videoCards: PersistedVideoCard[];
    /** Amenities step — persisted so selections survive page refresh */
    amenities: {
      /** Ordered list of selected staff role ids — order drives the translation section */
      selectedStaffOrder: string[];
      /** Custom roles created in the UI that don't exist in the API curated list */
      customRoles: StaffRoleOption[];
      /** Per-role translation map: roleId → { langCode → translated name } */
      staffTranslations: Record<string, Record<string, string>>;
      /** Per-role description, English. */
      staffDescriptions: Record<string, string>;
      /** Per-role description translation map: roleId → { langCode → translated description }. */
      staffDescriptionTranslations: Record<string, Record<string, string>>;
      /** Bookable add-on services/extras. */
      addons: { id: string; label: string }[];
      /** Per-addon translation map: addonId → { langCode → translated label }. */
      addonTranslations: Record<string, Record<string, string>>;
      /**
       * Per-category (property- or room-level) name translation map:
       * categoryId → { langCode → translated name }. Client-only — kept here
       * (not derived from GET) because AmenityCategoryResponse never echoes
       * `translations` back even though AmenityCategoryUpsertDto accepts and
       * stores it — confirmed live: the PATCH sends it, but a reload has
       * nothing to read it back from.
       */
      categoryNameTranslations: Record<string, Record<string, string>>;
      /**
       * Per-preloaded-amenity-item name translation map: the catalog item's
       * real `preloadedAmenityItemId` → { langCode → translated name }. Global
       * (not per-category) since the same catalog item means the same thing
       * everywhere it's selected — sent as this row's own `translations` on
       * every category using it (see localCategoryToPayload in useAmenitiesStep).
       */
      presetItemTranslations: Record<string, Record<string, string>>;
    };
  };
  updateAbout: (data: Partial<WizardState['draftProperty']>) => void;
  updateRating: (rating: number) => void;
  updateBasics: (data: Partial<WizardState['draftProperty']['basics']>) => void;
  updateEstate: (data: Partial<NonNullable<WizardState['draftProperty']['estate']>>) => void;
  updateSupplier: (data: Partial<NonNullable<WizardState['draftProperty']['supplier']>>) => void;
  updateAmenities: (data: Partial<WizardState["draftProperty"]["amenities"]>) => void;
  setVideoCards: (cards: PersistedVideoCard[]) => void;
  updateTranslations: (
    field: keyof WizardState['draftProperty']['translations'],
    variants: LanguageVariant[] | Record<string, any>
  ) => void;
  updateFeature: (
    index: number,
    data: Partial<WizardState['draftProperty']['features'][0]>
  ) => void;
  updateLocation: (
    data: Partial<WizardState['draftProperty']['location']>
  ) => void;
  // Photos
  addPhotoCategory: (label: string) => void;
  removePhotoCategory: (categoryId: string) => void;
  addPhotosToCategory: (
    categoryId: string,
    items: Pick<PhotoItem, 'id' | 'previewUrl'>[]
  ) => void;
  removePhoto: (categoryId: string, photoId: string) => void;
  toggleHeroPhoto: (categoryId: string, photoId: string) => void;
  resetWizard: () => void;
  /**
   * The currently-mounted step's own save function, registered by that step
   * on mount so "Save & exit" (in the header) can save whatever's pending on
   * the current screen before leaving — without every caller needing to know
   * which step it is or duplicate that step's own save/validation logic.
   * Deliberately not persisted: a function reference is only ever meaningful
   * for the step that registered it in this browser tab, this render.
   */
  saveCurrentStep: (() => Promise<boolean>) | null;
  setSaveCurrentStep: (fn: (() => Promise<boolean>) | null) => void;
}
/**
 * A fresh, empty draftProperty — used both as the store's initial state and to
 * reset on `setDraftIds`/`resetWizard`. A function (not a shared object
 * literal) so every reset gets its own arrays/objects rather than one mutable
 * reference reused across every property a user works on in the same session.
 */
function createInitialDraftProperty(): WizardState["draftProperty"] {
  return {
    name: "",
    code: "",
    description: "",
    subDescription: "",
    tagline: "",
    rating: 0,
    features: [
      { id: 1, icon: "star", title: "", desc: "" },
      { id: 2, icon: "star", title: "", desc: "" },
      { id: 3, icon: "star", title: "", desc: "" },
    ],
    estate: {
      interiorRooms: "",
      exteriorFeatures: "",
      customFields: [],
      floorFeatures: {},
    },
    supplier: {
      name: "",
      contactEmail: "",
      contactPhone: "",
      rating: 0,
      notes: "",
    },
    location: {
      addressLine: "", city: "", country: "", region: "", postalCode: "",
      lat: "0", lng: "0", countryCode: "", placeId: null, formattedAddress: "",
      floor: "", unit: "", building: "",
      scenicViews: [],
      customScenicViewIds: [],
      removedScenicViewIds: [],
      nearbyPlaces: [],
      locationFeatures: [],
    },
    basics: {
      bedrooms: 1,
      bathrooms: 1,
      maxGuests: 1,
      livingArea: "",
      landSize: "",
      floors: [],
      rooms: [],
    },
    translations: {
      name: [], description: [], subDescription: [], tagline: [], features: {},
      addressLine: [], city: [], country: [], region: [], postalCode: [],
      formattedAddress: [], floor: [], unit: [], building: [],
      scenicViews: {}, nearbyPlaces: {}, locationFeatures: {},
      floors: {}, rooms: {}, floorFeatures: {}, bedNames: {},
      interiorRooms: [], exteriorFeatures: [], customFields: {}, customFieldLabels: {},
      supplierName: [], supplierNotes: [],
      checkInTime: [], checkOutTime: [], quietHoursStart: [], quietHoursEnd: [],
      numberOfGuests: [], maxPets: [], additionalRules: [],
      hostName: [], hostLocation: [], hostBio: [],
      houseRules: {}, safetyDevices: {}, safetyConsiderations: {},
      accessibilityCommon: {}, accessibilityRooms: {}, paymentMethods: {},
      importantInformation: {},
    },
    photos: {
      propertyCategories: DEFAULT_PROPERTY_PHOTO_CATEGORIES.map((c) => ({
        ...c,
        photos: [],
      })),
    },
    videoCards: [],
    amenities: {
      selectedStaffOrder: [],
      customRoles: [],
      staffTranslations: {},
      staffDescriptions: {},
      staffDescriptionTranslations: {},
      addons: [],
      addonTranslations: {},
      categoryNameTranslations: {},
      presetItemTranslations: {},
    },
  };
}

export const useWizardStore = create<WizardState>()(persist((set) => ({
  propertyId: null,
  versionId: null,
  editable: true,
  status: null,
  completedStepIds: [],
  hydratedVersionId: null,
  markVersionHydrated: (vid) => set({ hydratedVersionId: vid }),
  invalidateWizardHydration: () => set({ hydratedVersionId: null }),
  stepSessionCache: {},
  setStepSessionCache: (key, value) => set((state) => ({ stepSessionCache: { ...state.stepSessionCache, [key]: value } })),
  saveCurrentStep: null,
  setSaveCurrentStep: (fn) => set({ saveCurrentStep: fn }),
  /**
   * Fully resets wizard state before starting a brand-new property — clears
   * propertyId/versionId/editable/status and gives draftProperty a fresh
   * object (not a shared reference), so nothing from whatever property was
   * last open leaks into the new one.
   */
  resetWizard: () => set({
    propertyId: null,
    versionId: null,
    editable: true,
    status: null,
    completedStepIds: [],
    hydratedVersionId: null,
    draftProperty: createInitialDraftProperty(),
  }),
  /**
   * Every call site passes a genuinely different (newly created, or freshly
   * opened) version — so draftProperty is reset to a clean slate here. Without
   * this, a brand-new "Create Villa" draft (or switching to a different
   * existing property) inherits whatever was last persisted locally for a
   * completely different property version.
   */
  setDraftIds: (propertyId, versionId) => set({ propertyId, versionId, completedStepIds: [], hydratedVersionId: null, draftProperty: createInitialDraftProperty() }),
  hydrateWizard: (apiData) => set((state) => {
    const { about, location, basics, estate, supplier, staffRoles } = apiData;
    // Only a full (no `section`) payload carries steps[] — a scoped hydrate
    // (a step re-fetching just its own section) leaves the rail's existing
    // checkmarks alone rather than wiping them.
    const completedStepIds = apiData.steps ? computeCompletedStepIds(apiData) : state.completedStepIds;

    // Up to 3 fixed feature slots (a UI design constant, not server-modeled) —
    // map by array position, keeping the existing default icon for any slot
    // the API doesn't provide.
    const features = state.draftProperty.features.map((existing, i) => {
      const apiFeature = about?.features?.[i];
      return apiFeature
        ? { id: existing.id, serverId: apiFeature.id, icon: apiFeature.iconKey || existing.icon, title: apiFeature.title, desc: apiFeature.subtext }
        : { ...existing, serverId: undefined, title: "", desc: "" };
    });

    // Re-key from server feature id to local slot id.
    const featureTranslations: Record<string, { title: LanguageVariant[]; subtext: LanguageVariant[] }> = {};
    features.forEach((f) => {
      const apiTranslations = f.serverId ? about?.featureTranslations?.[f.serverId] : undefined;
      featureTranslations[String(f.id)] = {
        title: extractFieldVariants(apiTranslations, "title"),
        subtext: extractFieldVariants(apiTranslations, "subtext"),
      };
    });

    // staffRoles from the server is authoritative for this version — always
    // overwrite (not "only if empty"): a non-empty local selection at this
    // point can only be leftover from a previously-open different version.
    const sortedStaffRoles = [...(staffRoles ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);

    // Every section's translations merge into this one object — a full
    // hydrate touches several sections at once, and each used to set its own
    // `translations` key on the same draftProperty literal, silently
    // clobbering the others (only the last one survived).
    const translations = {
      ...state.draftProperty.translations,
      ...(about ? {
        name: extractFieldVariants(about.translations, "name"),
        tagline: extractFieldVariants(about.translations, "tagline"),
        description: extractFieldVariants(about.translations, "description"),
        subDescription: extractFieldVariants(about.translations, "subDescription"),
        features: featureTranslations,
      } : {}),
      ...(estate ? {
        interiorRooms: extractFieldVariants(estate.translations, "interiorRooms"),
        exteriorFeatures: extractFieldVariants(estate.translations, "exteriorFeatures"),
        customFields: Object.fromEntries(
          (estate.customFields ?? []).map((c) => [c.id, extractFieldVariants(estate.customFieldTranslations?.[c.id], "content")]),
        ),
      } : {}),
      ...(location ? {
        addressLine: extractFieldVariants(location.translations, "addressLine"),
        floor: extractFieldVariants(location.translations, "floor"),
        unit: extractFieldVariants(location.translations, "aptUnit"),
        building: extractFieldVariants(location.translations, "building"),
        city: extractFieldVariants(location.translations, "city"),
        country: extractFieldVariants(location.translations, "country"),
        region: extractFieldVariants(location.translations, "region"),
        // Each term carries its own `translations.label`, same shape as nearbyPlaces below —
        // these were previously never re-extracted on hydrate, so reopening a version (which
        // resets the whole draft before rehydrating) silently lost them.
        scenicViews: Object.fromEntries(
          (location.scenicViews ?? []).map((t) => [t.termId, extractFieldVariants(t.translations, "label")]),
        ),
        locationFeatures: Object.fromEntries(
          (location.locationFeatures ?? []).map((t) => [t.termId, extractFieldVariants(t.translations, "label")]),
        ),
        // A live round-trip test (2026-09-21) confirmed these DO come back on GET — an
        // earlier belief that they never did was wrong (or the API changed since).
        nearbyPlaces: Object.fromEntries(
          (location.nearbyPlaces ?? []).map((p) => [
            p.id,
            {
              placeType: extractFieldVariants(p.translations, "placeType"),
              distance: extractFieldVariants(p.translations, "distance"),
              details: extractFieldVariants(p.translations, "details"),
            },
          ]),
        ),
      } : {}),
      ...(supplier ? {
        supplierName: extractFieldVariants(supplier.translations, "name"),
        supplierNotes: extractFieldVariants(supplier.translations, "notes"),
      } : {}),
      ...(basics ? {
        floors: Object.fromEntries(
          (basics.floors ?? []).map((f) => [f.id, extractFieldVariants(f.translations, "name")]),
        ),
        rooms: Object.fromEntries([
          ...(basics.floors ?? []).flatMap((f) => f.rooms.map((r) => [r.id, extractFieldVariants(r.translations, "name")] as const)),
          ...(basics.rooms ?? []).map((r) => [r.id, extractFieldVariants(r.translations, "name")] as const),
        ]),
        // Keyed by detail row id (not floor id) — matches estate.floorFeatures' own id above.
        floorFeatures: Object.fromEntries(
          (basics.floors ?? []).flatMap((f) => f.details.map((d) => [d.id, extractFieldVariants(d.translations, "name")] as const)),
        ),
        // Keyed by `${roomId}_${bedTypeTermId}`, same as BedSetupModal/roomToWire — never hydrating this wiped a bed's translation on every save.
        bedNames: Object.fromEntries([
          ...(basics.floors ?? []).flatMap((f) => f.rooms.flatMap((r) =>
            r.beds.map((b) => [`${r.id}_${b.bedTypeTermId}`, extractFieldVariants(b.translations, "label")] as const))),
          ...(basics.rooms ?? []).flatMap((r) =>
            r.beds.map((b) => [`${r.id}_${b.bedTypeTermId}`, extractFieldVariants(b.translations, "label")] as const)),
        ]),
      } : {}),
    };

    return {
      propertyId: apiData.propertyId ?? state.propertyId,
      versionId: apiData.versionId ?? state.versionId,
      editable: apiData.editable ?? state.editable,
      status: apiData.status ?? state.status,
      completedStepIds,
      draftProperty: {
          ...state.draftProperty,
          translations,
          // Server-issued, read-only (see AboutStep's Property Code display) — never
          // client-settable anywhere in the API, so this only ever reflects a real hydrate.
          code: apiData.code ?? state.draftProperty.code,
          ...(about ? {
            name: about.name ?? "",
            description: about.description ?? "",
            subDescription: about.subDescription ?? "",
            tagline: about.tagline ?? "",
            features,
          } : {}),
          ...(location ? {
            location: {
              addressLine: location.addressLine ?? "",
              city: location.city ?? "",
              country: location.country ?? "",
              region: location.region ?? "",
              postalCode: location.postalCode != null ? String(location.postalCode) : "",
              lat: location.lat != null ? String(location.lat) : "0",
              lng: location.lng != null ? String(location.lng) : "0",
              countryCode: location.countryCode ?? "",
              placeId: location.placeId ?? null,
              formattedAddress: location.formattedAddress ?? "",
              floor: location.floor ?? "",
              unit: location.aptUnit ?? "",
              building: location.building ?? "",
              scenicViews: (location.scenicViews ?? []).map((t) => t.termId),
              customScenicViewIds: (location.scenicViews ?? []).filter((t) => !t.curated).map((t) => t.termId),
              // Client-only — the server has no concept of this, so a hydrate must carry it forward, not reset it.
              removedScenicViewIds: state.draftProperty?.location?.removedScenicViewIds ?? [],
              nearbyPlaces: (location.nearbyPlaces ?? []).map((p) => ({
                id: p.id,
                placeType: p.placeType,
                distance: p.distance,
                details: p.details,
                icon: p.iconKey ?? "",
                lat: String(p.lat),
                lng: String(p.lng),
              })),
              locationFeatures: (location.locationFeatures ?? []).map((t) => ({
                id: t.termId,
                name: t.label,
                description: "",
                isCustom: !t.curated,
                isSelected: t.value,
              })),
            }
          } : {}),
          ...(basics ? {
            basics: {
              // Keep the server's real value (0/null for a never-touched version) —
              // masking it to 1 here made an incomplete step look already-valid and
              // already-saved: the step's own validation always passed (nothing was
              // ever really < 1 once masked), and the dirty-check baseline was
              // captured from the same masked value, so Next never detected a change
              // and never re-sent it. The property stayed stuck at bedrooms=0 on the
              // server forever while the UI showed 1. BasicsStep's own `|| 0` display
              // fallback still shows a real 0 as 0, prompting an actual edit.
              bedrooms: basics.bedrooms || 0,
              bathrooms: basics.bathrooms || 0,
              maxGuests: basics.maxGuests || 0,
              livingArea: basics.livingAreaSqft != null ? String(basics.livingAreaSqft) : "",
              landSize: basics.landSizeSqft != null ? String(basics.landSizeSqft) : "",
              floors: (basics.floors ?? []).map((f) => ({ id: f.id, name: f.name })),
              // Rooms come back nested under their floor (assigned) or in a
              // separate top-level list (unassigned) — flattened here into
              // one array with an explicit floorId, matching every other
              // part of this codebase's (and the old API's) flat room model.
              rooms: [
                ...(basics.floors ?? []).flatMap((f) =>
                  f.rooms.map((r) => ({
                    id: r.id,
                    name: r.name,
                    floorId: f.id,
                    size: r.size ?? undefined,
                    beds: Object.fromEntries(r.beds.map((b) => [b.bedTypeTermId, b.count])),
                  })),
                ),
                ...(basics.rooms ?? []).map((r) => ({
                  id: r.id,
                  name: r.name,
                  floorId: undefined,
                  size: r.size ?? undefined,
                  beds: Object.fromEntries(r.beds.map((b) => [b.bedTypeTermId, b.count])),
                })),
              ],
            }
          } : {}),
          ...(estate ? {
            estate: {
              interiorRooms: estate.interiorRooms ?? "",
              exteriorFeatures: estate.exteriorFeatures ?? "",
              customFields: (estate.customFields ?? []).map((c) => ({ id: c.id, heading: c.label, description: c.content })),
              // Per-floor highlights now round-trip via FloorInputDto.details
              // (BE added this field) — only refreshed when `basics` is part
              // of this same hydrate; a section-scoped hydrate for just
              // `estate` carries forward whatever's already in local state
              // instead of blowing it away.
              floorFeatures: basics
                ? Object.fromEntries(
                    (basics.floors ?? []).map((f) => [
                      f.id,
                      f.details.map((d) => ({ id: d.id, text: d.name, icon: d.iconKey ?? undefined })),
                    ]),
                  )
                : state.draftProperty.estate?.floorFeatures ?? {},
            },
          } : {}),
          ...(supplier ? {
            supplier: {
              name: supplier.name ?? "",
              contactEmail: supplier.contactEmail ?? "",
              contactPhone: supplier.contactPhone ?? "",
              rating: supplier.rating ?? 0,
              notes: supplier.notes ?? "",
            },
          } : {}),
          ...(apiData.rating !== undefined ? {
            rating: apiData.rating?.westayRating ?? 0,
          } : {}),
          ...(apiData.staffRoles !== undefined ? {
            amenities: {
              selectedStaffOrder: sortedStaffRoles.map((r) => r.staffRoleId),
              customRoles: sortedStaffRoles.filter((r) => !r.curated).map((r) => ({
                id: r.staffRoleId,
                name: r.label,
                icon: r.iconKey ?? r.key,
                isCustom: true,
              })),
              // description/translations are now real, saved fields
              // (StaffRoleInputDto/AddonInputDto) — these used to be
              // hardcoded to empty on every hydrate, which silently wiped
              // them out again right after the very save that wrote them
              // (a successful PATCH /amenities always triggers a refetch).
              staffTranslations: Object.fromEntries(
                sortedStaffRoles.map((r) => [r.staffRoleId, extractPlainFieldMap(r.translations, "label")]),
              ),
              staffDescriptions: Object.fromEntries(
                sortedStaffRoles.filter((r) => r.description).map((r) => [r.staffRoleId, r.description as string]),
              ),
              staffDescriptionTranslations: Object.fromEntries(
                sortedStaffRoles.map((r) => [r.staffRoleId, extractPlainFieldMap(r.translations, "description")]),
              ),
              addons: (apiData.addons ?? []).map((a) => ({ id: a.addonId, label: a.label })),
              addonTranslations: Object.fromEntries(
                (apiData.addons ?? []).map((a) => [a.addonId, extractPlainFieldMap(a.translations, "label")]),
              ),
              // GET's amenities section does carry these back (the old "never comes back" assumption was stale) — read them instead of only trusting local state.
              categoryNameTranslations: Object.fromEntries([
                ...(apiData.amenities?.property ?? []).map((c) => [c.id, extractPlainFieldMap(c.translations, "name")] as const),
                ...(apiData.amenities?.rooms ?? []).flatMap((r) => r.categories.map((c) => [c.id, extractPlainFieldMap(c.translations, "name")] as const)),
              ]),
              presetItemTranslations: Object.fromEntries([
                ...(apiData.amenities?.property ?? []).flatMap((c) => c.amenityItems),
                ...(apiData.amenities?.rooms ?? []).flatMap((r) => r.categories.flatMap((c) => c.amenityItems)),
              ].filter((item) => item.preloadedAmenityItemId)
                .map((item) => [item.preloadedAmenityItemId as string, extractPlainFieldMap(item.translations, "label")] as const)),
            }
          } : {}),
        },
    };
  }),
  draftProperty: createInitialDraftProperty(),
  updateAbout: (data) =>
    set((state) => ({
      draftProperty: { ...state.draftProperty, ...data },
    })),
  updateRating: (rating) =>
    set((state) => ({
      draftProperty: { ...state.draftProperty, rating },
    })),
  updateBasics: (data) =>
    set((state) => ({
      draftProperty: {
        ...state.draftProperty,
        basics: { ...state.draftProperty.basics, ...data },
      },
    })),
  updateEstate: (data) =>
    set((state) => ({
      draftProperty: {
        ...state.draftProperty,
        estate: state.draftProperty.estate
          ? { ...state.draftProperty.estate, ...data }
          : { interiorRooms: "", exteriorFeatures: "", customFields: [], floorFeatures: {}, ...data },
      },
    })),
  updateSupplier: (data) =>
    set((state) => ({
      draftProperty: {
        ...state.draftProperty,
        supplier: state.draftProperty.supplier
          ? { ...state.draftProperty.supplier, ...data }
          : { name: "", contactEmail: "", contactPhone: "", rating: 0, notes: "", ...data },
      },
    })),
  updateAmenities: (data) => set((state) => ({
    draftProperty: {
      ...state.draftProperty,
      amenities: { ...state.draftProperty.amenities, ...data },
    },
  })),
  setVideoCards: (cards) => set((state) => ({
    draftProperty: { ...state.draftProperty, videoCards: cards },
  })),
  updateLocation: (data) => set((state) => ({
    draftProperty: {
      ...state.draftProperty,
      location: { ...state.draftProperty.location, ...data }
    }
  })),
  updateTranslations: (field, variants) => set((state) => ({
    draftProperty: {
      ...state.draftProperty,
      translations: {
        ...state.draftProperty.translations,
        [field]: variants,
      }
    }
  })),
  updateFeature: (index, data) =>
    set((state) => {
      const newFeatures = [...state.draftProperty.features];
      newFeatures[index] = { ...newFeatures[index], ...data };
      return {
        draftProperty: { ...state.draftProperty, features: newFeatures },
      };
    }),

  addPhotoCategory: (label) =>
    set((state) => ({
      draftProperty: {
        ...state.draftProperty,
        photos: {
          ...state.draftProperty.photos,
          propertyCategories: [
            ...state.draftProperty.photos.propertyCategories,
            { id: crypto.randomUUID(), label, required: false, photos: [] },
          ],
        },
      },
    })),

  removePhotoCategory: (categoryId) =>
    set((state) => ({
      draftProperty: {
        ...state.draftProperty,
        photos: {
          ...state.draftProperty.photos,
          propertyCategories:
            state.draftProperty.photos.propertyCategories.filter(
              (c) => c.id !== categoryId,
            ),
        },
      },
    })),

  addPhotosToCategory: (categoryId, items) =>
    set((state) => ({
      draftProperty: {
        ...state.draftProperty,
        photos: {
          ...state.draftProperty.photos,
          propertyCategories: state.draftProperty.photos.propertyCategories.map(
            (c) =>
              c.id !== categoryId
                ? c
                : {
                    ...c,
                    photos: [
                      ...c.photos,
                      ...items.map((item) => ({ ...item, isHero: false })),
                    ],
                  },
          ),
        },
      },
    })),

  removePhoto: (categoryId, photoId) =>
    set((state) => ({
      draftProperty: {
        ...state.draftProperty,
        photos: {
          ...state.draftProperty.photos,
          propertyCategories: state.draftProperty.photos.propertyCategories.map(
            (c) =>
              c.id !== categoryId
                ? c
                : { ...c, photos: c.photos.filter((p) => p.id !== photoId) },
          ),
        },
      },
    })),

  toggleHeroPhoto: (categoryId, photoId) =>
    set((state) => {
      const allPhotos = state.draftProperty.photos.propertyCategories.flatMap(
        (c) => c.photos,
      );
      const currentHeroCount = allPhotos.filter((p) => p.isHero).length;

      return {
        draftProperty: {
          ...state.draftProperty,
          photos: {
            ...state.draftProperty.photos,
            propertyCategories:
              state.draftProperty.photos.propertyCategories.map((c) =>
                c.id !== categoryId
                  ? c
                  : {
                      ...c,
                      photos: c.photos.map((p) => {
                        if (p.id !== photoId) return p;
                        if (!p.isHero && currentHeroCount >= PHOTOS_MAX_HERO)
                          return p;
                        return { ...p, isHero: !p.isHero };
                      }),
                    },
              ),
          },
        },
      };
    }),
}), {
  name: "wizard-storage",
  version: 13,
  // hydratedVersionId is tab-session bookkeeping, not draft data — persisting
  // it would make a fresh page load wrongly think a version's already been
  // fetched and skip the real fetch, serving stale localStorage data instead.
  // stepSessionCache is the same kind of bookkeeping — same-session only.
  partialize: (state) => ({ ...state, hydratedVersionId: null, stepSessionCache: {} }),
  migrate(persisted: unknown, fromVersion: number) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let state = persisted as any;

    if (fromVersion < 2) {
      // v2: add photos slice
      state = {
        ...state,
        draftProperty: {
          ...state.draftProperty,
          photos: {
            propertyCategories: DEFAULT_PROPERTY_PHOTO_CATEGORIES.map((c) => ({
              ...c,
              photos: [],
            })),
          },
        },
      };
    }

    if (fromVersion < 3) {
      // v3: add amenities slice (selectedStaffOrder, customRoles, staffTranslations)
      state = {
        ...state,
        draftProperty: {
          ...state.draftProperty,
          amenities: {
            selectedStaffOrder: [],
            customRoles: [],
            staffTranslations: {},
            staffDescriptions: {},
            staffDescriptionTranslations: {},
            addons: [],
            addonTranslations: {},
          },
        },
      };
    }

    if (fromVersion < 4) {
      // v4: add videos array (superseded by videoCards in v5)
      state = {
        ...state,
        draftProperty: { ...state.draftProperty, videos: [] },
      };
    }

    if (fromVersion < 5) {
      // v5: replace videos[] with videoCards[] (richer shape with full card state)
      state = {
        ...state,
        draftProperty: { ...state.draftProperty, videoCards: [] },
      };
    }

    if (fromVersion < 6) {
      // v6: add Rules-step label-variant translations slice — without this,
      // an in-progress draft's stale translations object (persisted before
      // these keys existed) has no houseRules/safetyDevices/etc. at all,
      // and reading translations.houseRules[termId] throws.
      const existing = state.draftProperty?.translations ?? {};
      state = {
        ...state,
        draftProperty: {
          ...state.draftProperty,
          translations: {
            ...existing,
            checkInTime: existing.checkInTime ?? [],
            checkOutTime: existing.checkOutTime ?? [],
            quietHoursStart: existing.quietHoursStart ?? [],
            quietHoursEnd: existing.quietHoursEnd ?? [],
            numberOfGuests: existing.numberOfGuests ?? [],
            additionalRules: existing.additionalRules ?? [],
            houseRules: existing.houseRules ?? {},
            safetyDevices: existing.safetyDevices ?? {},
            safetyConsiderations: existing.safetyConsiderations ?? {},
            accessibilityCommon: existing.accessibilityCommon ?? {},
            accessibilityRooms: existing.accessibilityRooms ?? {},
            paymentMethods: existing.paymentMethods ?? {},
          },
        },
      };
    }

    if (fromVersion < 7) {
      // v7: add maxPets label-variant translations slice (Rules step, pets_allowed toggle).
      const existing = state.draftProperty?.translations ?? {};
      state = {
        ...state,
        draftProperty: {
          ...state.draftProperty,
          translations: {
            ...existing,
            maxPets: existing.maxPets ?? [],
          },
        },
      };
    }

    if (fromVersion < 8) {
      // v8: add subDescription (About step), plus its translations slice.
      const existing = state.draftProperty?.translations ?? {};
      state = {
        ...state,
        draftProperty: {
          ...state.draftProperty,
          subDescription: state.draftProperty?.subDescription ?? "",
          translations: {
            ...existing,
            subDescription: existing.subDescription ?? [],
          },
        },
      };
    }

    if (fromVersion < 9) {
      // v9: add categoryNameTranslations (Amenities step) — client-only, see its own doc comment above.
      state = {
        ...state,
        draftProperty: {
          ...state.draftProperty,
          amenities: {
            ...state.draftProperty?.amenities,
            categoryNameTranslations: state.draftProperty?.amenities?.categoryNameTranslations ?? {},
          },
        },
      };
    }

    if (fromVersion < 10) {
      // v10: add presetItemTranslations (Amenities step) — client-only, see its own doc comment above.
      state = {
        ...state,
        draftProperty: {
          ...state.draftProperty,
          amenities: {
            ...state.draftProperty?.amenities,
            presetItemTranslations: state.draftProperty?.amenities?.presetItemTranslations ?? {},
          },
        },
      };
    }

    if (fromVersion < 11) {
      // v11: add floorFeatures translations slice (Basics floor-plan step's floor-detail rows — API-backed via FloorDetailInputDto.translations).
      const existing = state.draftProperty?.translations ?? {};
      state = {
        ...state,
        draftProperty: {
          ...state.draftProperty,
          translations: {
            ...existing,
            floorFeatures: existing.floorFeatures ?? {},
          },
        },
      };
    }

    // Note: nearbyPlaces translations grew back from {placeType, details} to
    // {placeType, distance, details} (distance is translatable again, confirmed
    // live) — no migration needed: getPlaceTranslations in NearbyPlaces.tsx
    // defaults a missing `distance` key to `[]` for any draft saved before this.

    if (fromVersion < 12) {
      // v12: add location.customScenicViewIds — server-confirmed (curated: false) scenic
      // view ids as of last hydrate, needed since a saved custom view becomes a real,
      // permanent taxonomy term and stops being distinguishable from a seeded one by id.
      state = {
        ...state,
        draftProperty: {
          ...state.draftProperty,
          location: {
            ...state.draftProperty?.location,
            customScenicViewIds: state.draftProperty?.location?.customScenicViewIds ?? [],
          },
        },
      };
    }

    if (fromVersion < 13) {
      // v13: add location.removedScenicViewIds — client-only list of custom scenic
      // views this property has deleted, hiding them from the picker.
      state = {
        ...state,
        draftProperty: {
          ...state.draftProperty,
          location: {
            ...state.draftProperty?.location,
            removedScenicViewIds: state.draftProperty?.location?.removedScenicViewIds ?? [],
          },
        },
      };
    }

    return state as WizardState;
  },
}));
